import React, { useState } from 'react'
import useSWRMutation from 'swr/mutation'
import { postFetcher } from 'api/swr'
import { Button } from '@/components/ui/button'
import Loader from '@/components/ui/Loader'
import { ReloadIcon, PlusIcon, ClipboardIcon } from '@radix-ui/react-icons'
import { Form } from '@/components/ui/form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { MyFormInput, MyFormSelect } from 'components/ui/MyFormField'
import { useToast } from '@/components/ui/use-toast'
import { ToastAction } from '@/components/ui/toast'
import { z } from 'zod'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const DataTableToolbar = ({ mutate, role }) => {
  return (
    <div className="flex items-center justify-between mb-2">
      <Button variant="outline" onClick={() => mutate()}>
        <ReloadIcon className="h-4 w-4 mr-2" />
        Refetch
      </Button>
      <div className="flex items-center gap-2">
        {role === 'painter' ? (
          <CreatePainter mutate={mutate} />
        ) : (
          <CreateAdminDesigner mutate={mutate} />
        )}
      </div>
    </div>
  )
}

const CreateRoleOptions = [
  { name: 'admin', code: 'admin' },
  { name: 'designer', code: 'designer' },
]

const CreateAdminDesigner = ({ mutate }) => {
  const { toast } = useToast()
  const [isOpen, setIsOpen] = useState()
  const [defaultValues] = useState({
    username: '',
    nick_name: '',
    role: '',
  })

  const { trigger, isMutating, error } = useSWRMutation(
    '/publishers',
    postFetcher,
  )

  const infoFormSchema = z.object({
    username: z.string().email(),
    nick_name: z.string().min(2).max(30),
    role: z.string().min(1),
  })
  const form = useForm({
    resolver: zodResolver(infoFormSchema),
    defaultValues,
  })

  const onSubmit = (data) => {
    if (isMutating) return
    trigger(data, {
      onSuccess: (res) => {
        mutate()
        setIsOpen(false)
        if (res.password) {
          toast({
            title: 'Success',
            description: `The publisher ${res.username} password is ${res.password}.`,
            action: (
              <ToastAction
                altText="Try again"
                onClick={() =>
                  navigator.clipboard.writeText(
                    `username:${res.username};password:${res.password};`,
                  )
                }
              >
                <ClipboardIcon className="w-4 h-4 mr-2" />
                Copy ID
              </ToastAction>
            ),
          })
        }
      },
    })
  }
  return (
    <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
        <DialogTrigger asChild>
          <Button>
            <PlusIcon className="mr-2 h-4 w-4" />
            Create
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Admin / Designer</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <MyFormInput name="username" form={form} label="user name" />
              <MyFormInput name="nick_name" form={form} label="nick name" />
              <MyFormSelect
                name="role"
                form={form}
                label="role"
                items={CreateRoleOptions}
              />

              {isMutating ? (
                <Loader>Submit...</Loader>
              ) : (
                <Button type="submit">Submit</Button>
              )}
              {error && <div className="text-red-500">{error.message}</div>}
            </form>
          </Form>
        </DialogContent>
    </Dialog>
  )
}

export default DataTableToolbar

const CreatePainter = ({ mutate }) => {
  const { toast } = useToast()
  const [isOpen, setIsOpen] = useState()
  const [photoUrl, setPhotoUrl] = useState('')

  const { trigger, isMutating, error } = useSWRMutation(
    '/publishers/painter',
    postFetcher,
  )

  const infoFormSchema = z.object({
    nick_name: z.string().min(2).max(30),
  })
  const form = useForm({
    resolver: zodResolver(infoFormSchema),
    defaultValues: { nick_name: '' },
  })

  const onSubmit = (data) => {
    if (isMutating) return
    if (!photoUrl) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please paste an avatar image URL',
      })
      return
    }
    trigger(
      { ...data, photoUrl },
      {
        onSuccess: () => {
          mutate()
          setIsOpen(false)
          setPhotoUrl('')
          toast({ title: 'Success', description: 'Painter created' })
        },
      },
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
        <DialogTrigger asChild>
          <Button>
            <PlusIcon className="mr-2 h-4 w-4" />
            Create Painter
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Painter</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <MyFormInput name="nick_name" form={form} label="nick name" />
              <MyFormInput
                name="photoUrl"
                form={form}
                label="avatar url"
                onChange={(e) => setPhotoUrl(e.target.value)}
              />
              {photoUrl ? (
                <img
                  alt="preview"
                  className="aspect-square rounded-md border h-24 w-24"
                  src={photoUrl}
                />
              ) : null}
              {isMutating ? (
                <Loader>Submit...</Loader>
              ) : (
                <Button type="submit">Submit</Button>
              )}
              {error && <div className="text-red-500">{error.message}</div>}
            </form>
          </Form>
        </DialogContent>
    </Dialog>
  )
}
